import { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Trash2, Download, FileText, Settings, Pencil, Send, Receipt, Upload, ImageIcon, Link as LinkIcon,
} from "lucide-react";
import { fmtEur2, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const STATUS_LABEL = {
  bozza: "Bozza", inviato: "Inviato", accettato: "Accettato",
  rifiutato: "Rifiutato", scaduto: "Scaduto",
};
const STATUS_COLOR = {
  bozza: "bg-zen-subtle/40 text-zen-ink",
  inviato: "bg-zen-info/15 text-zen-info",
  accettato: "bg-zen-primary/15 text-zen-primary",
  rifiutato: "bg-zen-alert/15 text-zen-alert",
  scaduto: "bg-zen-alert/15 text-zen-alert",
};

const emptyItem = () => ({ description: "", quantity: 1, unit_price: 0 });
const emptyQuote = () => {
  const today = new Date().toISOString().slice(0, 10);
  const valid = new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10);
  return {
    client_id: "", project_id: "", title: "Preventivo",
    issued_date: today, valid_until: valid,
    items: [emptyItem()], iva_pct: 22, discount_pct: 0,
    notes: "", terms: "", status: "bozza",
  };
};

const emptyProfile = {
  company_name: "", company_vat: "", company_tax_code: "",
  company_address: "", company_city: "", company_zip: "", company_country: "Italia",
  company_email: "", company_phone: "", company_iban: "", default_iva_pct: 22,
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(emptyQuote());
  const [profileOpen, setProfileOpen] = useState(false);

  const load = async () => {
    const [q, c, p, prof] = await Promise.all([
      api.get("/quotes"),
      api.get("/clients"),
      api.get("/projects"),
      api.get("/profile"),
    ]);
    setQuotes(q.data);
    setClients(c.data);
    setProjects(p.data);
    setProfile({ ...emptyProfile, ...(prof.data || {}) });
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => {
    const c = clients.find((x) => x.client_id === id);
    if (!c) return "—";
    return c.company || c.name || "—";
  };

  const openNew = () => {
    setEditing({ ...emptyQuote(), client_id: clients[0]?.client_id || "" });
    setEditorOpen(true);
  };
  const openEdit = (q) => {
    setEditing({
      ...q,
      items: (q.items && q.items.length > 0) ? q.items : [emptyItem()],
      project_id: q.project_id || "",
      notes: q.notes || "",
      terms: q.terms || "",
    });
    setEditorOpen(true);
  };

  const updItem = (idx, patch) => {
    setEditing((e) => ({
      ...e,
      items: e.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const addItem = () => setEditing((e) => ({ ...e, items: [...e.items, emptyItem()] }));
  const rmItem = (idx) => setEditing((e) => ({
    ...e, items: e.items.length > 1 ? e.items.filter((_, i) => i !== idx) : e.items,
  }));

  const totals = useMemo(() => {
    const sub = editing.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
    const disc = sub * ((parseFloat(editing.discount_pct) || 0) / 100);
    const taxable = sub - disc;
    const iva = taxable * ((parseFloat(editing.iva_pct) || 0) / 100);
    return { sub, disc, taxable, iva, total: taxable + iva };
  }, [editing.items, editing.iva_pct, editing.discount_pct]);

  const save = async () => {
    if (!editing.client_id) return toast.error("Seleziona un cliente");
    if (editing.items.length === 0 || editing.items.every(i => !i.description)) {
      return toast.error("Aggiungi almeno una voce con descrizione");
    }
    setLoading(true);
    try {
      const payload = {
        ...editing,
        project_id: editing.project_id || null,
        items: editing.items.map(i => ({
          description: i.description || "",
          quantity: parseFloat(i.quantity) || 0,
          unit_price: parseFloat(i.unit_price) || 0,
        })),
        iva_pct: parseFloat(editing.iva_pct) || 0,
        discount_pct: parseFloat(editing.discount_pct) || 0,
      };
      if (editing.quote_id) {
        await api.put(`/quotes/${editing.quote_id}`, payload);
        toast.success("Preventivo aggiornato");
      } else {
        await api.post("/quotes", payload);
        toast.success("Preventivo creato");
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    } finally { setLoading(false); }
  };

  const remove = async (id, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm("Eliminare questo preventivo?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Preventivo eliminato");
    load();
  };

  const download = async (q, ev) => {
    ev?.stopPropagation?.();
    try {
      const origin = encodeURIComponent(window.location.origin);
      const res = await api.get(`/quotes/${q.quote_id}/pdf?origin=${origin}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Preventivo_${q.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Errore download"));
    }
  };

  const copyLink = async (q, ev) => {
    ev?.stopPropagation?.();
    const link = `${window.location.origin}/q/${q.public_token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link firma copiato negli appunti");
    } catch {
      window.prompt("Copia questo link:", link);
    }
  };

  const markSent = async (q, ev) => {
    ev?.stopPropagation?.();
    await api.put(`/quotes/${q.quote_id}`, { status: "inviato" });
    toast.success("Preventivo segnato come inviato");
    load();
  };

  const toInvoice = async (q, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm(`Trasformare il preventivo ${q.number} in fattura? Il preventivo verrà segnato come accettato.`)) return;
    try {
      const { data } = await api.post(`/quotes/${q.quote_id}/to-invoice`);
      toast.success(`Fattura ${data.number} creata`);
      load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo troppo grande (max 3MB)");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, company_logo_url: data.company_logo_url }));
      toast.success("Logo caricato");
    } catch (e) { toast.error(errMsg(e, "Upload fallito")); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Rimuovere il logo?")) return;
    await api.delete("/profile/logo");
    setProfile((p) => ({ ...p, company_logo_url: null }));
    toast.success("Logo rimosso");
  };

  const saveProfile = async () => {
    try {
      await api.put("/profile", {
        ...profile,
        default_iva_pct: parseFloat(profile.default_iva_pct) || 22,
      });
      toast.success("Dati azienda salvati");
      setProfileOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  const missingProfile = !profile.company_name || !profile.company_vat;

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Preventivi</span>
          <h1 className="mt-2 font-heading text-3xl">Preventivi & Documenti</h1>
          <p className="text-zen-ink2 mt-1">
            {quotes.length} preventivi · scarica in PDF pronto per la firma.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="open-profile-btn"
            variant="outline"
            onClick={() => setProfileOpen(true)}
            className="rounded-full"
          >
            <Settings size={14} className="mr-1.5" /> Dati azienda
          </Button>
          <Button
            data-testid="add-quote-btn"
            onClick={openNew}
            className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
            disabled={clients.length === 0}
          >
            <Plus size={16} className="mr-1" /> Nuovo preventivo
          </Button>
        </div>
      </div>

      {missingProfile && (
        <div
          data-testid="profile-warning"
          className="zen-card p-4 border-zen-info/30 bg-zen-info/5 flex items-start gap-3"
        >
          <FileText size={18} className="text-zen-info shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <b>Prima di creare un preventivo</b> — compila i dati della tua azienda
            (nome + P.IVA) così appariranno correttamente in intestazione al PDF.
            <button
              data-testid="profile-warning-btn"
              onClick={() => setProfileOpen(true)}
              className="ml-2 text-zen-primary hover:underline"
            >
              Compila ora →
            </button>
          </div>
        </div>
      )}

      {clients.length === 0 && (
        <div className="zen-card p-6 text-center text-zen-ink2">
          Aggiungi prima un cliente per creare un preventivo.
        </div>
      )}

      <div className="zen-card overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-zen-bg text-zen-ink2">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Numero</th>
              <th className="text-left px-5 py-3 font-medium">Cliente</th>
              <th className="text-left px-5 py-3 font-medium">Titolo</th>
              <th className="text-right px-5 py-3 font-medium">Totale</th>
              <th className="text-left px-5 py-3 font-medium">Valido fino</th>
              <th className="text-left px-5 py-3 font-medium">Stato</th>
              <th className="text-right px-5 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zen-border">
            {quotes.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-zen-ink2">
                  Nessun preventivo ancora — crea il primo.
                </td>
              </tr>
            ) : quotes.map(q => {
              const total = (q.items || []).reduce(
                (s, i) => s + (i.quantity * i.unit_price), 0);
              const disc = total * (q.discount_pct / 100);
              const taxable = total - disc;
              const gross = taxable * (1 + q.iva_pct / 100);
              return (
                <tr
                  key={q.quote_id}
                  data-testid={`quote-${q.quote_id}`}
                  onClick={() => openEdit(q)}
                  className="hover:bg-zen-bg/50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-4 font-medium text-zen-ink flex items-center gap-2">
                    <Pencil size={12} className="text-zen-ink2 opacity-60" />
                    {q.number}
                  </td>
                  <td className="px-5 py-4">{clientName(q.client_id)}</td>
                  <td className="px-5 py-4 text-zen-ink2 truncate max-w-xs">{q.title}</td>
                  <td className="px-5 py-4 text-right font-medium">{fmtEur2(gross)}</td>
                  <td className="px-5 py-4 text-zen-ink2">{fmtDate(q.valid_until)}</td>
                  <td className="px-5 py-4">
                    <Badge className={`${STATUS_COLOR[q.status]} rounded-full font-normal`}>
                      {STATUS_LABEL[q.status]}
                    </Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex gap-2 justify-end">
                      <Button
                        data-testid={`pdf-${q.quote_id}`}
                        size="sm" variant="outline"
                        onClick={(e) => download(q, e)}
                        className="rounded-full"
                      >
                        <Download size={13} className="mr-1" /> PDF
                      </Button>
                      {q.public_token && (
                        <Button
                          data-testid={`link-${q.quote_id}`}
                          size="sm" variant="outline"
                          onClick={(e) => copyLink(q, e)}
                          className="rounded-full"
                          title="Copia link pubblico di firma"
                        >
                          <LinkIcon size={13} className="mr-1" /> Link firma
                        </Button>
                      )}
                      {q.status === "bozza" && (
                        <Button
                          data-testid={`send-${q.quote_id}`}
                          size="sm"
                          onClick={(e) => markSent(q, e)}
                          className="bg-zen-primary hover:bg-zen-primaryHover rounded-full"
                        >
                          <Send size={13} className="mr-1" /> Inviato
                        </Button>
                      )}
                      {(q.status === "accettato" || q.status === "inviato") && (
                        <Button
                          data-testid={`toinvoice-${q.quote_id}`}
                          size="sm" variant="outline"
                          onClick={(e) => toInvoice(q, e)}
                          className="rounded-full border-zen-primary/30 text-zen-primary hover:bg-zen-primary/5"
                          title="Crea fattura da questo preventivo"
                        >
                          <Receipt size={13} className="mr-1" /> Fattura
                        </Button>
                      )}
                      <button
                        data-testid={`del-quote-${q.quote_id}`}
                        onClick={(e) => remove(q.quote_id, e)}
                        className="text-zen-alert hover:opacity-70"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3" data-testid="quotes-mobile-list">
        {quotes.length === 0 ? (
          <div className="zen-card p-8 text-center text-zen-ink2">Nessun preventivo ancora — crea il primo.</div>
        ) : quotes.map((q) => {
          const total = (q.items || []).reduce((s, i) => s + (i.quantity * i.unit_price), 0);
          const disc = total * (q.discount_pct / 100);
          const taxable = total - disc;
          const gross = taxable * (1 + q.iva_pct / 100);
          return (
            <div
              key={q.quote_id}
              data-testid={`quote-m-${q.quote_id}`}
              className="zen-card p-4 space-y-3"
              onClick={() => openEdit(q)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-zen-ink2">{q.number}</div>
                  <div className="font-medium text-zen-ink truncate">{clientName(q.client_id)}</div>
                  <div className="text-sm text-zen-ink2 truncate">{q.title}</div>
                </div>
                <Badge className={`${STATUS_COLOR[q.status]} rounded-full font-normal shrink-0`}>{STATUS_LABEL[q.status]}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-zen-ink2">{fmtDate(q.valid_until)}</span>
                <span className="font-medium text-zen-ink">{fmtEur2(gross)}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-zen-border" onClick={(e) => e.stopPropagation()}>
                <Button data-testid={`m-pdf-${q.quote_id}`} size="sm" variant="outline" onClick={(e)=>download(q, e)} className="rounded-full">
                  <Download size={13} className="mr-1"/> PDF
                </Button>
                {q.public_token && (
                  <Button data-testid={`m-link-${q.quote_id}`} size="sm" variant="outline" onClick={(e)=>copyLink(q, e)} className="rounded-full">
                    <LinkIcon size={13} className="mr-1"/> Link firma
                  </Button>
                )}
                {q.status === "bozza" && (
                  <Button data-testid={`m-send-${q.quote_id}`} size="sm" onClick={(e)=>markSent(q, e)} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
                    <Send size={13} className="mr-1"/> Inviato
                  </Button>
                )}
                {(q.status === "accettato" || q.status === "inviato") && (
                  <Button data-testid={`m-toinvoice-${q.quote_id}`} size="sm" variant="outline" onClick={(e)=>toInvoice(q, e)} className="rounded-full border-zen-primary/30 text-zen-primary hover:bg-zen-primary/5">
                    <Receipt size={13} className="mr-1"/> Fattura
                  </Button>
                )}
                <button data-testid={`m-del-quote-${q.quote_id}`} onClick={(e)=>remove(q.quote_id, e)} className="ml-auto text-zen-alert hover:opacity-70 p-2">
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="quote-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle>{editing.quote_id ? "Modifica preventivo" : "Nuovo preventivo"}</DialogTitle>
            <DialogDescription className="sr-only">
              Compila i dati del preventivo, aggiungi le voci e scarica il PDF.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Cliente *</Label>
                <Select
                  value={editing.client_id}
                  onValueChange={(v) => setEditing({ ...editing, client_id: v })}
                >
                  <SelectTrigger data-testid="qe-client"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name || "—"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Progetto (opzionale)</Label>
                <Select
                  value={editing.project_id || "_none"}
                  onValueChange={(v) => setEditing({ ...editing, project_id: v === "_none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nessuno</SelectItem>
                    {projects.filter(p => !editing.client_id || p.client_id === editing.client_id)
                      .map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Titolo</Label>
              <Input
                data-testid="qe-title"
                value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Es. Restyling sito web"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Numero</Label>
                <Input
                  data-testid="qe-number"
                  value={editing.number || ""}
                  onChange={(e) => setEditing({ ...editing, number: e.target.value })}
                  placeholder="Auto"
                />
              </div>
              <div>
                <Label>Emesso il</Label>
                <Input
                  type="date"
                  value={editing.issued_date || ""}
                  onChange={(e) => setEditing({ ...editing, issued_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Valido fino al</Label>
                <Input
                  type="date"
                  value={editing.valid_until || ""}
                  onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })}
                />
              </div>
            </div>

            {/* Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Voci</Label>
                <Button
                  data-testid="qe-add-item"
                  size="sm" variant="outline" onClick={addItem}
                  className="rounded-full h-8"
                >
                  <Plus size={14} className="mr-1" /> Aggiungi voce
                </Button>
              </div>
              <div className="space-y-2">
                {editing.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start p-3 border border-zen-border rounded-xl">
                    <div className="col-span-12 md:col-span-6">
                      <Label className="text-xs">Descrizione</Label>
                      <Textarea
                        data-testid={`qe-item-desc-${idx}`}
                        value={it.description}
                        onChange={(e) => updItem(idx, { description: e.target.value })}
                        rows={2}
                        placeholder="Es. Sviluppo homepage responsive"
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs">Quantità</Label>
                      <Input
                        type="number" step="0.5" min="0"
                        data-testid={`qe-item-qty-${idx}`}
                        value={it.quantity}
                        onChange={(e) => updItem(idx, { quantity: e.target.value })}
                      />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <Label className="text-xs">Prezzo unit. (€)</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        data-testid={`qe-item-price-${idx}`}
                        value={it.unit_price}
                        onChange={(e) => updItem(idx, { unit_price: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1 flex items-end justify-end">
                      <button
                        data-testid={`qe-item-rm-${idx}`}
                        onClick={() => rmItem(idx)}
                        className="text-zen-alert hover:opacity-70 h-9"
                        disabled={editing.items.length === 1}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Sconto (%)</Label>
                <Input
                  type="number" step="0.5" min="0" max="100"
                  data-testid="qe-discount"
                  value={editing.discount_pct}
                  onChange={(e) => setEditing({ ...editing, discount_pct: e.target.value })}
                />
              </div>
              <div>
                <Label>IVA (%)</Label>
                <Input
                  type="number" step="0.5" min="0" max="100"
                  data-testid="qe-iva"
                  value={editing.iva_pct}
                  onChange={(e) => setEditing({ ...editing, iva_pct: e.target.value })}
                />
              </div>
              <div>
                <Label>Stato</Label>
                <Select
                  value={editing.status}
                  onValueChange={(v) => setEditing({ ...editing, status: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bozza">Bozza</SelectItem>
                    <SelectItem value="inviato">Inviato</SelectItem>
                    <SelectItem value="accettato">Accettato</SelectItem>
                    <SelectItem value="rifiutato">Rifiutato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Totals live */}
            <div className="p-4 rounded-xl bg-zen-bg" data-testid="qe-totals">
              <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                <span className="text-zen-ink2">Subtotale</span>
                <span className="text-right">{fmtEur2(totals.sub)}</span>
                {totals.disc > 0 && (
                  <>
                    <span className="text-zen-ink2">Sconto {editing.discount_pct}%</span>
                    <span className="text-right">− {fmtEur2(totals.disc)}</span>
                    <span className="text-zen-ink2">Imponibile</span>
                    <span className="text-right">{fmtEur2(totals.taxable)}</span>
                  </>
                )}
                <span className="text-zen-ink2">IVA {editing.iva_pct}%</span>
                <span className="text-right">{fmtEur2(totals.iva)}</span>
                <span className="font-heading text-lg text-zen-primary pt-2 border-t border-zen-border mt-1">Totale</span>
                <span className="font-heading text-lg text-zen-primary text-right pt-2 border-t border-zen-border mt-1" data-testid="qe-total">
                  {fmtEur2(totals.total)}
                </span>
              </div>
            </div>

            <div>
              <Label>Note</Label>
              <Textarea
                data-testid="qe-notes"
                rows={2}
                value={editing.notes || ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                placeholder="Es. 30% acconto alla firma, saldo alla consegna."
              />
            </div>
            <div>
              <Label>Termini e condizioni</Label>
              <Textarea
                data-testid="qe-terms"
                rows={3}
                value={editing.terms || ""}
                onChange={(e) => setEditing({ ...editing, terms: e.target.value })}
                placeholder="Es. Consegna in 4 settimane. Il codice sorgente resta di proprietà del cliente al saldo."
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {editing.quote_id && (
              <Button
                variant="ghost"
                onClick={(e) => remove(editing.quote_id, e)}
                data-testid="qe-delete"
                className="text-zen-alert hover:text-zen-alert/80 hover:bg-zen-alert/5 sm:mr-auto"
              >
                <Trash2 size={14} className="mr-1" /> Elimina
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setEditorOpen(false)}>Annulla</Button>
              <Button
                data-testid="qe-save"
                onClick={save}
                disabled={loading}
                className="bg-zen-primary hover:bg-zen-primaryHover"
              >
                {loading ? "Salvo…" : "Salva"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company profile dialog */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="profile-dialog"
        >
          <DialogHeader>
            <DialogTitle>Dati azienda</DialogTitle>
            <DialogDescription>
              Questi dati compaiono in intestazione ai tuoi preventivi PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            {/* Logo uploader */}
            <div>
              <Label>Logo aziendale (opzionale, appare nel PDF)</Label>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <div className="w-24 h-24 rounded-xl border border-zen-border bg-zen-bg flex items-center justify-center overflow-hidden shrink-0">
                  {profile.company_logo_url ? (
                    <img
                      data-testid="pf-logo-preview"
                      src={profile.company_logo_url}
                      alt="Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <ImageIcon size={28} className="text-zen-ink2 opacity-40"/>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-zen-border text-sm hover:bg-zen-subtle/20">
                    <Upload size={14}/> Carica logo
                    <input
                      data-testid="pf-logo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadLogo(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {profile.company_logo_url && (
                    <Button
                      data-testid="pf-logo-remove"
                      variant="ghost" size="sm"
                      onClick={removeLogo}
                      className="text-zen-alert hover:bg-zen-alert/5"
                    >
                      <Trash2 size={13} className="mr-1"/> Rimuovi
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-zen-ink2 mt-1.5">PNG, JPG, GIF o WEBP · max 3MB · verrà scalato in alto a sinistra del PDF.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Ragione sociale *</Label>
                <Input
                  data-testid="pf-name"
                  value={profile.company_name || ""}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                />
              </div>
              <div>
                <Label>P.IVA *</Label>
                <Input
                  data-testid="pf-vat"
                  value={profile.company_vat || ""}
                  onChange={(e) => setProfile({ ...profile, company_vat: e.target.value })}
                />
              </div>
              <div>
                <Label>Codice fiscale</Label>
                <Input
                  value={profile.company_tax_code || ""}
                  onChange={(e) => setProfile({ ...profile, company_tax_code: e.target.value })}
                />
              </div>
              <div>
                <Label>IBAN</Label>
                <Input
                  data-testid="pf-iban"
                  value={profile.company_iban || ""}
                  onChange={(e) => setProfile({ ...profile, company_iban: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Indirizzo</Label>
              <Input
                value={profile.company_address || ""}
                onChange={(e) => setProfile({ ...profile, company_address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>CAP</Label>
                <Input
                  value={profile.company_zip || ""}
                  onChange={(e) => setProfile({ ...profile, company_zip: e.target.value })}
                />
              </div>
              <div>
                <Label>Città</Label>
                <Input
                  value={profile.company_city || ""}
                  onChange={(e) => setProfile({ ...profile, company_city: e.target.value })}
                />
              </div>
              <div>
                <Label>Paese</Label>
                <Input
                  value={profile.company_country || "Italia"}
                  onChange={(e) => setProfile({ ...profile, company_country: e.target.value })}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profile.company_email || ""}
                  onChange={(e) => setProfile({ ...profile, company_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input
                  value={profile.company_phone || ""}
                  onChange={(e) => setProfile({ ...profile, company_phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>IVA di default (%)</Label>
              <Input
                type="number"
                step="0.5" min="0" max="100"
                value={profile.default_iva_pct ?? 22}
                onChange={(e) => setProfile({ ...profile, default_iva_pct: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProfileOpen(false)}>Annulla</Button>
            <Button
              data-testid="pf-save"
              onClick={saveProfile}
              className="bg-zen-primary hover:bg-zen-primaryHover"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
